import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { 
  isInternal, 
  getDisplayContacts,
  getContactCounts 
} from "@/utils/contactFilters";
import { 
  Search, 
  Plus, 
  User, 
  Users,
  Phone, 
  Building2, 
  Mail, 
  Star,
  StarOff,
  Edit2,
  Trash2,
  Upload,
  Filter,
  X,
  Tag,
  MoreVertical,
  FileText,
  RefreshCw,
  Cloud,
  Wifi,
  UserCircle,
  Globe,
  Camera,
  ChevronDown
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AddContactModal from "@/components/AddContactModal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

import { API_URL } from '../utils/api';
const API = API_URL;

// Source badge colors and labels
const sourceConfig = {
  hero: { 
    color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    label: "PBX Directory",
    icon: Wifi
  },
  hero_phonebook: { 
    color: "bg-teal-500/20 text-teal-400 border-teal-500/30",
    label: "Hero Phonebook",
    icon: Cloud
  },
  company: { 
    color: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    label: "Company",
    icon: Building2
  },
  personal: { 
    color: "bg-purple-500/20 text-purple-400 border-purple-500/30",
    label: "Personal",
    icon: User
  },
  // Legacy support
  local: { 
    color: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    label: "Local",
    icon: User
  },
  import: { 
    color: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    label: "Imported",
    icon: Upload
  },
};

// Helper to get source display info
const getSourceInfo = (source) => sourceConfig[source] || sourceConfig.local;

function ContactCard({ contact, onEdit, onDelete, onToggleFavorite, onCall, onPushToHero, profileImage, onImageUpload, canEditImage, canPushToHero }) {
  return (
    <Card className="bg-card border-border/50 hover:border-primary/30 transition-all group">
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <div className="relative">
            <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center shrink-0 overflow-hidden">
              {profileImage ? (
                <img 
                  src={profileImage} 
                  alt={contact.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <User className="w-6 h-6 text-primary" />
              )}
            </div>
            {/* Camera icon overlay for admins */}
            {canEditImage && (
              <button
                onClick={() => onImageUpload(contact)}
                className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                data-testid={`edit-contact-image-${contact.id}`}
              >
                <Camera className="w-4 h-4 text-white" />
              </button>
            )}
          </div>
          
          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-medium truncate">{contact.name}</h3>
              {contact.favorite && (
                <Star className="w-4 h-4 text-amber-500 fill-amber-500 shrink-0" />
              )}
            </div>
            
            <div className="flex items-center gap-2 mt-1">
              <Phone className="w-3 h-3 text-muted-foreground" />
              <span className="text-sm font-mono text-muted-foreground">{contact.phone_number}</span>
              {contact.extension && (
                <Badge variant="secondary" className="text-xs font-mono px-1.5 py-0">
                  ext. {contact.extension}
                </Badge>
              )}
            </div>
            
            {contact.company && (
              <div className="flex items-center gap-2 mt-1">
                <Building2 className="w-3 h-3 text-muted-foreground" />
                <span className="text-sm text-muted-foreground truncate">{contact.company}</span>
              </div>
            )}
            
            {contact.email && (
              <div className="flex items-center gap-2 mt-1">
                <Mail className="w-3 h-3 text-muted-foreground" />
                <span className="text-sm text-muted-foreground truncate">{contact.email}</span>
              </div>
            )}
            
            {/* Tags & Source */}
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <Badge variant="outline" className={`text-xs ${getSourceInfo(contact.source).color}`}>
                {getSourceInfo(contact.source).label}
              </Badge>
              {contact.tags?.filter(t => t !== 'internal' && t !== 'pbx').map(tag => (
                <Badge key={tag} variant="outline" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8"
              onClick={() => onCall(contact)}
              data-testid={`call-contact-${contact.id}`}
            >
              <Phone className="w-4 h-4 text-emerald-500" />
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`contact-menu-${contact.id}`}>
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onToggleFavorite(contact)}>
                  {contact.favorite ? (
                    <><StarOff className="w-4 h-4 mr-2" /> Remove from favorites</>
                  ) : (
                    <><Star className="w-4 h-4 mr-2" /> Add to favorites</>
                  )}
                </DropdownMenuItem>
                {contact.source !== 'hero' && (
                  <DropdownMenuItem onClick={() => onEdit(contact)}>
                    <Edit2 className="w-4 h-4 mr-2" /> Edit
                  </DropdownMenuItem>
                )}
                {/* Push to Hero option for non-Hero contacts */}
                {canPushToHero && contact.source !== 'hero' && contact.source !== 'hero_phonebook' && !contact.synced_to_hero && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => onPushToHero(contact.id)}>
                      <Cloud className="w-4 h-4 mr-2" /> Push to Hero
                    </DropdownMenuItem>
                  </>
                )}
                {contact.source !== 'hero' && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={() => onDelete(contact)}
                      className="text-red-500 focus:text-red-500"
                    >
                      <Trash2 className="w-4 h-4 mr-2" /> Delete
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Contacts() {
  const { token, user, isCustomerAdmin } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [showFavorites, setShowFavorites] = useState(false);
  const [sources, setSources] = useState(["local"]);
  
  // Load default contact filter from localStorage (same as Dialer)
  const getDefaultContactFilter = () => {
    return localStorage.getItem('defaultContactFilter') || 'all';
  };
  const [activeTab, setActiveTab] = useState(getDefaultContactFilter);
  
  // User profile images (keyed by phone number)
  const [userImages, setUserImages] = useState({});
  
  // Map of phone numbers to user IDs (for image upload)
  const [phoneToUserId, setPhoneToUserId] = useState({});
  
  // Image upload dialog state
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedContactForImage, setSelectedContactForImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef(null);
  
  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    phone_number: "",
    company: "",
    email: "",
    notes: "",
    tags: [],
    source: "company"  // Default to company (shared) for new contacts
  });
  const [tagInput, setTagInput] = useState("");
  
  // CSV import
  const fileInputRef = useRef(null);
  const [csvData, setCsvData] = useState([]);
  const [csvPreview, setCsvPreview] = useState([]);
  
  // PBX sync
  const [pbxSyncing, setPbxSyncing] = useState(false);
  const [phonebookSyncing, setPhonebookSyncing] = useState(false);
  const [contactStats, setContactStats] = useState({ hero: 0, hero_phonebook: 0, company: 0, personal: 0, total: 0 });
  const [lastSyncInfo, setLastSyncInfo] = useState(null);
  const autoSyncTriggered = useRef(false);

  useEffect(() => {
    fetchContacts();
    fetchSources();
    fetchUserImages();
    fetchContactStats();
    checkAndAutoSync();
  }, [sourceFilter, showFavorites]);
  
  const fetchContacts = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append("search", searchQuery);
      if (sourceFilter !== "all") params.append("source", sourceFilter);
      if (showFavorites) params.append("favorite", "true");
      
      const res = await axios.get(`${API}/contacts?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(res.data);
    } catch (error) {
      console.error("Failed to fetch contacts:", error);
      toast.error("Failed to load contacts");
    } finally {
      setLoading(false);
    }
  };
  
  const fetchContactStats = async () => {
    try {
      const res = await axios.get(`${API}/contacts/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContactStats(res.data);
    } catch (error) {
      console.error("Failed to fetch contact stats:", error);
    }
  };
  
  const checkAndAutoSync = async () => {
    if (autoSyncTriggered.current) return;
    autoSyncTriggered.current = true;
    
    try {
      const res = await axios.get(`${API}/company/contacts-sync-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLastSyncInfo(res.data);
      
      if (res.data.needs_sync && isCustomerAdmin) {
        // Auto-sync in the background
        console.log(`Auto-syncing contacts (reason: ${res.data.reason})`);
        syncFromHeroPhonebook(true);
      }
    } catch (error) {
      console.error("Failed to check sync status:", error);
    }
  };
  
  const syncFromPBX = async () => {
    setPbxSyncing(true);
    try {
      const res = await axios.post(`${API}/contacts/sync-from-pbx`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message || `Synced ${res.data.total} contacts from PBX`);
      fetchContacts();
      fetchContactStats();
    } catch (error) {
      console.error("Failed to sync from PBX:", error);
      toast.error(error.response?.data?.detail || "Failed to sync from PBX");
    } finally {
      setPbxSyncing(false);
    }
  };
  
  const syncFromHeroPhonebook = async (silent = false) => {
    setPhonebookSyncing(true);
    try {
      const res = await axios.post(`${API}/company/sync-hero-contacts`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data;
      const total = (data.synced || 0) + (data.updated || 0);
      
      if (total > 0) {
        toast.success(`Synced ${data.synced || 0} new, updated ${data.updated || 0} contacts from PBX phonebook`);
        fetchContacts();
      } else if (!silent) {
        toast.info(data.message || "Contacts are up to date");
      }
      fetchContactStats();
      
      // Update sync info
      setLastSyncInfo(prev => ({
        ...prev,
        needs_sync: false,
        last_sync: new Date().toISOString(),
        hours_since_sync: 0,
        reason: "recent"
      }));
    } catch (error) {
      console.error("Failed to sync from Hero phonebook:", error);
      if (!silent) {
        toast.error(error.response?.data?.detail || "Failed to sync from PBX phonebook");
      }
    } finally {
      setPhonebookSyncing(false);
    }
  };
  
  // Push a single contact to Hero
  const pushContactToHero = async (contactId) => {
    try {
      const res = await axios.post(`${API}/contacts/push-to-hero/${contactId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        toast.success(res.data.message || "Contact pushed to Hero phonebook");
        fetchContacts();
      } else if (res.data.skipped) {
        toast.info(res.data.message || "Contact already synced");
      } else {
        toast.error(res.data.error || "Failed to push contact");
      }
    } catch (error) {
      console.error("Failed to push contact to Hero:", error);
      toast.error(error.response?.data?.detail || "Failed to push contact to Hero");
    }
  };
  
  const fetchUserImages = async () => {
    try {
      const res = await axios.get(`${API}/users/info/by-phone`);
      const data = res.data || {};
      
      // Extract images and user IDs separately
      const images = {};
      const userIds = {};
      for (const [phone, info] of Object.entries(data)) {
        if (info.profile_image) {
          images[phone] = info.profile_image;
        }
        userIds[phone] = info.id;
      }
      
      setUserImages(images);
      setPhoneToUserId(userIds);
    } catch (error) {
      console.error("Failed to fetch user images:", error);
    }
  };
  
  // Handle image upload for a contact
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedContactForImage) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const userId = phoneToUserId[selectedContactForImage.phone_number];
          
          if (userId) {
            // This contact is a registered user - update user profile image
            console.log('Uploading to user:', userId);
            await axios.put(
              `${API}/users/${userId}/profile-image`,
              { image_data: reader.result },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } else {
            // External/internal contact not registered - update contact image directly
            console.log('Uploading to contact:', selectedContactForImage.id, selectedContactForImage.name);
            const response = await axios.put(
              `${API}/contacts/${selectedContactForImage.id}/image`,
              { image_data: reader.result },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            console.log('Upload response:', response.data);
          }
          
          toast.success(`Profile image updated for ${selectedContactForImage.name}`);
          setImageDialogOpen(false);
          setSelectedContactForImage(null);
          fetchUserImages();
          fetchContacts(); // Refresh contacts to get updated images
        } catch (error) {
          console.error('Upload error:', error.response?.data || error.message);
          toast.error(error.response?.data?.detail || 'Failed to upload image');
        } finally {
          setUploadingImage(false);
        }
      };
      reader.onerror = (error) => {
        console.error('FileReader error:', error);
        toast.error('Failed to read image file');
        setUploadingImage(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Process error:', error);
      toast.error('Failed to process image');
      setUploadingImage(false);
    }

    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  // Remove contact profile image
  const handleRemoveContactImage = async () => {
    if (!selectedContactForImage) return;
    
    const userId = phoneToUserId[selectedContactForImage.phone_number];
    
    setUploadingImage(true);
    try {
      if (userId) {
        // This contact is a registered user - remove user profile image
        await axios.delete(
          `${API}/users/${userId}/profile-image`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // External contact - remove contact image directly
        await axios.delete(
          `${API}/contacts/${selectedContactForImage.id}/image`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      toast.success(`Profile image removed for ${selectedContactForImage.name}`);
      setImageDialogOpen(false);
      setSelectedContactForImage(null);
      fetchUserImages();
      fetchContacts(); // Refresh contacts to get updated images
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove image');
    } finally {
      setUploadingImage(false);
    }
  };

  // Get the image for a contact (user image takes priority, then contact image)
  const getContactImage = (contact) => {
    return userImages[contact.phone_number] || contact.profile_image;
  };

  // Open image dialog for a contact
  const openImageDialog = (contact) => {
    setSelectedContactForImage(contact);
    setImageDialogOpen(true);
  };

  const fetchSources = async () => {
    try {
      const res = await axios.get(`${API}/contacts/sources/list`);
      setSources(res.data);
    } catch (error) {
      console.error("Failed to fetch sources:", error);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchContacts();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const openCreateDialog = () => {
    setSelectedContact(null);
    setFormData({
      name: "",
      phone_number: "",
      company: "",
      email: "",
      notes: "",
      tags: []
    });
    setTagInput("");
    setEditDialogOpen(true);
  };

  const openEditDialog = (contact) => {
    setSelectedContact(contact);
    setFormData({
      name: contact.name,
      phone_number: contact.phone_number,
      company: contact.company || "",
      email: contact.email || "",
      notes: contact.notes || "",
      tags: contact.tags || []
    });
    setTagInput("");
    setEditDialogOpen(true);
  };

  const handleSaveContact = async () => {
    if (!formData.name || !formData.phone_number) {
      toast.error("Name and phone number are required");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (selectedContact) {
        // Update - don't include source in update (can't change source type)
        const updateData = { ...formData };
        delete updateData.source;
        await axios.put(`${API}/contacts/${selectedContact.id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Contact updated");
      } else {
        // Create - include source
        await axios.post(`${API}/contacts`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Contact created as ${formData.source === 'personal' ? 'personal' : 'company'} contact`);
      }
      setEditDialogOpen(false);
      fetchContacts();
      fetchSources();
      fetchContactStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save contact");
    }
  };

  const handleDeleteContact = async () => {
    if (!selectedContact) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/contacts/${selectedContact.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Contact deleted");
      setDeleteDialogOpen(false);
      setSelectedContact(null);
      fetchContacts();
      fetchContactStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete contact");
    }
  };

  const handleToggleFavorite = async (contact) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/contacts/${contact.id}/favorite`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchContacts();
    } catch (error) {
      toast.error("Failed to update favorite");
    }
  };

  const handleCall = (contact) => {
    // Navigate to softphone with the number
    window.location.href = `/app/dialer?number=${encodeURIComponent(contact.phone_number)}`;
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tagInput.trim()]
      });
      setTagInput("");
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(t => t !== tagToRemove)
    });
  };

  // CSV Import handlers
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result;
      parseCSV(text);
    };
    reader.readAsText(file);
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      toast.error("CSV file must have a header row and at least one data row");
      return;
    }

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const data = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || "";
      });
      data.push(row);
    }

    setCsvData(data);
    setCsvPreview(data.slice(0, 5));
    setImportDialogOpen(true);
  };

  const handleImportCSV = async () => {
    try {
      const res = await axios.post(`${API}/contacts/import`, {
        contacts: csvData,
        source: "import"
      });
      toast.success(`Imported ${res.data.imported} contacts (${res.data.skipped} skipped)`);
      setImportDialogOpen(false);
      setCsvData([]);
      setCsvPreview([]);
      fetchContacts();
      fetchSources();
    } catch (error) {
      toast.error("Failed to import contacts");
    }
  };

  const confirmDelete = (contact) => {
    setSelectedContact(contact);
    setDeleteDialogOpen(true);
  };

  // Use centralized contact filters - excludes test contacts and call queues
  const displayContacts = getDisplayContacts(contacts);
  const { total: totalContacts, internal: internalCount, external: externalCount } = getContactCounts(contacts);
  const favoriteCount = displayContacts.filter(c => c.favorite).length;
  const personalCount = displayContacts.filter(c => c.source === 'personal').length;

  const filteredByTab = displayContacts.filter(contact => {
    if (activeTab === "internal") return isInternal(contact);
    if (activeTab === "external") return !isInternal(contact);
    if (activeTab === "personal") return contact.source === 'personal';
    return true; // "all"
  });

  return (
    <div className="p-4 sm:p-6" data-testid="contacts-page">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">Contacts</h1>
          <p className="text-muted-foreground text-sm">
            {totalContacts} total • {internalCount} internal • {externalCount} external
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <input
            type="file"
            accept=".csv"
            ref={fileInputRef}
            onChange={handleFileSelect}
            className="hidden"
          />
          <Button 
            variant="outline" 
            onClick={() => fileInputRef.current?.click()}
            className="gap-2"
            data-testid="import-csv-btn"
          >
            <Upload className="w-4 h-4" />
            Import CSV
          </Button>
          <Button onClick={openCreateDialog} className="gap-2" data-testid="add-contact-btn">
            <Plus className="w-4 h-4" />
            Add Contact
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="grid w-full grid-cols-4 lg:w-[500px]">
          <TabsTrigger value="all" className="gap-2" data-testid="tab-all">
            <Users className="w-4 h-4" />
            All ({totalContacts})
          </TabsTrigger>
          <TabsTrigger value="internal" className="gap-2" data-testid="tab-internal">
            <UserCircle className="w-4 h-4" />
            Internal ({internalCount})
          </TabsTrigger>
          <TabsTrigger value="external" className="gap-2" data-testid="tab-external">
            <Globe className="w-4 h-4" />
            External ({externalCount})
          </TabsTrigger>
          <TabsTrigger value="personal" className="gap-2" data-testid="tab-personal">
            <User className="w-4 h-4" />
            Personal ({personalCount})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filters */}
      <Card className="bg-card border-border/50 mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search contacts..."
                value={searchQuery}
                onChange={handleSearch}
                className="pl-10 bg-secondary/50"
                data-testid="search-contacts"
              />
            </div>
            
            {/* Source Filter */}
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="w-full sm:w-[180px] bg-secondary/50" data-testid="source-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="All Sources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Contacts ({contactStats.total})</SelectItem>
                <SelectItem value="hero">PBX Directory ({contactStats.hero})</SelectItem>
                <SelectItem value="hero_phonebook">Hero Phonebook ({contactStats.hero_phonebook})</SelectItem>
                <SelectItem value="company">Company Shared ({contactStats.company})</SelectItem>
                <SelectItem value="personal">My Personal ({contactStats.personal})</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Sync Buttons */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  disabled={pbxSyncing || phonebookSyncing}
                  className="gap-2"
                  data-testid="sync-dropdown-btn"
                >
                  <RefreshCw className={`w-4 h-4 ${(pbxSyncing || phonebookSyncing) ? "animate-spin" : ""}`} />
                  Sync
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={syncFromPBX} disabled={pbxSyncing}>
                  <Wifi className="w-4 h-4 mr-2" />
                  Sync Internal Users
                </DropdownMenuItem>
                {isCustomerAdmin && (
                  <DropdownMenuItem onClick={() => syncFromHeroPhonebook(false)} disabled={phonebookSyncing}>
                    <Cloud className="w-4 h-4 mr-2" />
                    Sync PBX Phonebook
                  </DropdownMenuItem>
                )}
                {lastSyncInfo?.last_sync && (
                  <>
                    <DropdownMenuSeparator />
                    <div className="px-2 py-1.5 text-xs text-muted-foreground">
                      Last sync: {lastSyncInfo.hours_since_sync < 1 
                        ? "less than 1 hour ago" 
                        : `${Math.round(lastSyncInfo.hours_since_sync)}h ago`}
                      {lastSyncInfo.last_count ? ` (${lastSyncInfo.last_count} contacts)` : ""}
                    </div>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* Favorites Toggle */}
            <Button
              variant={showFavorites ? "default" : "outline"}
              onClick={() => setShowFavorites(!showFavorites)}
              className="gap-2"
              data-testid="favorites-filter"
            >
              <Star className={`w-4 h-4 ${showFavorites ? "fill-current" : ""}`} />
              Favorites
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Contact Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Card key={i} className="bg-card border-border/50 animate-pulse">
              <CardContent className="p-4 h-32" />
            </Card>
          ))}
        </div>
      ) : filteredByTab.length === 0 ? (
        <Card className="bg-card border-border/50">
          <CardContent className="p-12 text-center">
            <User className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {activeTab === "internal" ? "No internal contacts" : 
               activeTab === "external" ? "No external contacts" : 
               "No contacts found"}
            </h3>
            <p className="text-muted-foreground text-sm mb-4">
              {searchQuery || sourceFilter !== "all" || showFavorites
                ? "Try adjusting your filters"
                : activeTab === "internal" 
                  ? "Internal contacts are team members synced from Hero"
                  : activeTab === "external"
                    ? "External contacts are clients and other contacts"
                    : "Add your first contact to get started"}
            </p>
            {!searchQuery && sourceFilter === "all" && !showFavorites && activeTab === "all" && (
              <Button onClick={openCreateDialog} className="gap-2">
                <Plus className="w-4 h-4" />
                Add Contact
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredByTab.map(contact => (
            <ContactCard
              key={contact.id}
              contact={contact}
              onEdit={openEditDialog}
              onDelete={confirmDelete}
              onToggleFavorite={handleToggleFavorite}
              onCall={handleCall}
              onPushToHero={pushContactToHero}
              profileImage={getContactImage(contact)}
              onImageUpload={openImageDialog}
              canEditImage={isCustomerAdmin()}
              canPushToHero={isCustomerAdmin()}
            />
          ))}
        </div>
      )}

      {/* Create/Edit Contact Modal */}
      <AddContactModal
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        contact={selectedContact}
        onSaved={() => {
          fetchContacts();
          setSelectedContact(null);
        }}
      />

      {/* CSV Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Import Contacts</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              Preview of {csvData.length} contacts to import:
            </p>
            
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50">
                  <tr>
                    <th className="px-3 py-2 text-left">Name</th>
                    <th className="px-3 py-2 text-left">Phone</th>
                    <th className="px-3 py-2 text-left">Company</th>
                  </tr>
                </thead>
                <tbody>
                  {csvPreview.map((row, i) => (
                    <tr key={i} className="border-t border-border/50">
                      <td className="px-3 py-2">{row.name}</td>
                      <td className="px-3 py-2 font-mono text-xs">{row.phone_number || row.phone}</td>
                      <td className="px-3 py-2">{row.company || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {csvData.length > 5 && (
                <div className="px-3 py-2 bg-secondary/30 text-xs text-muted-foreground">
                  ... and {csvData.length - 5} more
                </div>
              )}
            </div>
            
            <p className="text-xs text-muted-foreground mt-4">
              Duplicate phone numbers will be skipped. Contacts will be tagged as &quot;import&quot; source.
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleImportCSV} data-testid="confirm-import">
              Import {csvData.length} Contacts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Contact</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{selectedContact?.name}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteContact}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Image Upload Dialog */}
      <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-primary" />
              Edit Profile Image
            </DialogTitle>
          </DialogHeader>
          
          {selectedContactForImage && (
            <div className="space-y-4">
              {/* Contact Info */}
              <div className="text-center">
                <p className="text-sm text-muted-foreground">
                  Update profile image for <strong>{selectedContactForImage.name}</strong>
                </p>
                <p className="text-xs text-muted-foreground font-mono mt-1">
                  {selectedContactForImage.phone_number}
                </p>
                {phoneToUserId[selectedContactForImage.phone_number] && (
                  <Badge variant="secondary" className="mt-2 text-xs">Registered User</Badge>
                )}
              </div>
              
              {/* Current Image Preview */}
              <div className="flex justify-center">
                {getContactImage(selectedContactForImage) ? (
                  <img 
                    src={getContactImage(selectedContactForImage)} 
                    alt={selectedContactForImage.name} 
                    className="w-24 h-24 rounded-full object-cover border-4 border-border"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-primary/20 flex items-center justify-center border-4 border-border">
                    <User className="w-10 h-10 text-primary" />
                  </div>
                )}
              </div>

              {/* Hidden file input */}
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                data-testid="contact-image-file-input"
              />

              {/* Action Buttons */}
              <div className="flex flex-col gap-2">
                <Button
                  onClick={() => imageInputRef.current?.click()}
                  disabled={uploadingImage}
                  className="w-full"
                  data-testid="upload-contact-image-btn"
                >
                  {uploadingImage ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  {getContactImage(selectedContactForImage) ? 'Change Image' : 'Upload Image'}
                </Button>

                {getContactImage(selectedContactForImage) && (
                  <Button
                    variant="outline"
                    onClick={handleRemoveContactImage}
                    disabled={uploadingImage}
                    className="w-full text-destructive hover:text-destructive"
                    data-testid="remove-contact-image-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remove Image
                  </Button>
                )}
              </div>

              <p className="text-xs text-muted-foreground text-center">
                Accepted formats: JPG, PNG, GIF, WebP (max 5MB)
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
